import { Dispatch, MouseEvent, SetStateAction } from "react";
import { AcAirflowRequestsModelsDagRequestsEcleanEcleanCurationStage, AcAirflowRequestsModelsDagRequestsEcleanEcleanCurationStageStageType, ACPredictDAGRequestModel, ACSinglePredictDAGRequestModel, ACTrainModelDAGRequestModel, Dataset, DatasetVisualizationDAGRequestModel, EcleanCurationDAGRequestModel, EcleanCurationDAGRequestModelOutputDatasetTypesItem, EcleanCurationDAGRequestModelSpecifiedColumnAnnotations, ReadAcrossDAGRequestModel, UploadDataFileRequest } from "../../api/models";
import { useDatasetVisualizationResultsetDatasetVisualizationPost, useEcleanCurationDatasetEcleanCurationPost, usePredictResultsetPredictPost, useReadacrossPredictReadacrossPredictPost, useSinglePredictResultsetSinglePredictPost, useTrainModelModelTrainPost, useUploadDataFileDataFileUploadPost } from "../../api/generated/fastAPI";
import { SpecifyColumnMetadataType } from "../../routes/Datasets/components/GenerateDatasetModal";
import { CurationTypes, EcleanCuration } from "../../routes/Datasets/components/GenerateDSComponents/Curate";

const usePost = ({
    setIsLoading,
    setIsDisabled,
    setIsSuccess,
    setPopupMessage,
    refetchFn,
    handleClose,
}: {
    setIsLoading: Dispatch<SetStateAction<boolean>>,
    setIsDisabled: Dispatch<SetStateAction<boolean>>, 
    setIsSuccess?: Dispatch<SetStateAction<boolean>>, 
    setPopupMessage: (message: string, isSuccess: boolean) => void,
    refetchFn?: () => void,
    handleClose?: () => void,
}) => {
    const uploadFileMutation = useUploadDataFileDataFileUploadPost();
    const generateDatasetMutation = useEcleanCurationDatasetEcleanCurationPost();
    const curationMutation = useEcleanCurationDatasetEcleanCurationPost();
    const trainModelMutation = useTrainModelModelTrainPost();
    const batchPredictionMutation = usePredictResultsetPredictPost();
    const singlePredictionMutation = useSinglePredictResultsetSinglePredictPost();
    const readacrossPredictionMutation = useReadacrossPredictReadacrossPredictPost();
    const generateVisualizationMutation = useDatasetVisualizationResultsetDatasetVisualizationPost();

    const handleUploadFile = async (
        e: MouseEvent<HTMLButtonElement>, 
        selectedTab: string,
        fileForm: UploadDataFileRequest, 
        fileUploaded: File | null, 
        casListInput: string,
    ) => {
        e.preventDefault();
        

        const metadata: UploadDataFileRequest = {
            name: fileForm.name!,
            file_metadata: {
                tags: fileForm.file_metadata!.tags ? fileForm.file_metadata!.tags : null,
            },
            datafile_metadata: {
                description: fileForm.datafile_metadata!.description ? fileForm.datafile_metadata!.description : undefined,
                measurement_type: fileForm.datafile_metadata!.measurement_type ? fileForm.datafile_metadata!.measurement_type : undefined,
                target: fileForm.datafile_metadata!.target ? fileForm.datafile_metadata!.target : undefined,
                organism: fileForm.datafile_metadata!.organism ? fileForm.datafile_metadata!.organism : undefined,
            }
        }


        if (selectedTab === 'Import file' && fileUploaded) {
            setIsLoading(true);
            setIsDisabled(true);
            try {
                await uploadFileMutation.mutateAsync({
                    data: {
                        uploaded_file: fileUploaded,
                        metadata,
                    }
                });
                setPopupMessage('Your file has been uploaded successfully!', true);
                refetchFn && refetchFn();
                handleClose && handleClose();
            } catch (err:any) {
                console.log(err);
                setPopupMessage('We encountered an issue uploading this file. Please try again.', false);
            }
            setIsLoading(false);
            setIsDisabled(false);
        } else if (selectedTab === 'Convert CAS list' && casListInput) {
            setIsLoading(true);
            setIsDisabled(true);
            try {
                const file = await convertCasList(e, casListInput, fileForm);
                if (file) {
                    await uploadFileMutation.mutateAsync({
                        data: {
                            uploaded_file: file,
                            metadata,
                        }
                    })
                    setPopupMessage('Your datafile has been uploaded successfully!', true);
                    refetchFn && refetchFn();
                    handleClose && handleClose();
                }
            } catch (err:any) {
                console.log(err);
                setPopupMessage('We encountered an issue uploading this datafile. Please try again.', false);
            }
            setIsLoading(false);
            setIsDisabled(false);
        }
    }

    const convertCasList = async (e:any, casListInput: string, fileForm: UploadDataFileRequest) => {
        const trimmedList = casListInput.replace(/\s/g, '');
        const pipeSeparatedList = trimmedList.replaceAll(',', '|');
        const encodedString = encodeURIComponent(pipeSeparatedList);
        
        try {
            // const response = await customInstance({
            //     url: `https://cdxapps.epa.gov/oms-substance-registry-services/rest-api/substances/cas?casList=${encodedString}`,
            //     method: 'GET',
            // })
            const response = await fetch(`https://cdxapps.epa.gov/oms-substance-registry-services/rest-api/substances/cas?casList=${encodedString}`, {
                method: 'GET',
            })
            const data = await response.json() as any[];
            const smilesArray = data.map((d:any) => d.smilesNotation);
            const totalString = 'SMILES\n' + smilesArray.join('\n');
            const newFile = new File([totalString], fileForm.name!, {type: "text/csv"});

            // const noRecordList: string[] | null = compareNewFileToCasList(pipeSeparatedList.split('|'), data.map((d:any) => d.currentCasNumber));

            return newFile;
        } catch (err:any) {
            console.log(err);
            return null;
        }
    };

    const getColumnAnnotations = (metadataForm: SpecifyColumnMetadataType) => {
        let specified_column_annotations: EcleanCurationDAGRequestModelSpecifiedColumnAnnotations = {};

        const { activityColumns, chemicalName, structure, other, recordId } = metadataForm;

        activityColumns.forEach(col => {
            if (col.assignedColumn) {
                specified_column_annotations[col.assignedColumn] = {
                    description: col.description ? col.description : undefined,
                    data_type: col.data_type,
                }
            }
        });

        if (chemicalName.assignedColumn) {
            specified_column_annotations[chemicalName.assignedColumn] = {
                description: chemicalName.description ? chemicalName.description : undefined,
                data_type: 'chemical-name'
            }
        }
        
        if (structure.assignedColumn) {
            specified_column_annotations[structure.assignedColumn] = {
                description: structure.description ? structure.description : undefined,
                data_type: 'structure'
            }
        }

        if (recordId.assignedColumn) {
            specified_column_annotations[recordId.assignedColumn] = {
                description: recordId.description ? recordId.description : undefined,
                data_type: 'record-id'
            }
        }

        if (other.length > 0) {
            other.forEach(o => {
                if (o.assignedColumn) {
                    specified_column_annotations[o.assignedColumn] = {
                        description: o.description ? o.description : undefined,
                        data_type: 'other'
                    }
                }
            })
        }

        return specified_column_annotations;
    };

    const formatEcleanStages = (curationForm: EcleanCuration) => {
        let stages: AcAirflowRequestsModelsDagRequestsEcleanEcleanCurationStage[] = [];

        let lower_bound: number | null = null;
        let upper_bound: number | null = null;

        let binarization_units: string | null = null;
        let units_out: string | null = null;

        if (curationForm.eclean_std_values) {
            if (!isNaN(Number(curationForm.eclean_std_values.value_range.lower_bound)) && curationForm.eclean_std_values.value_range.lower_bound !== null) {
                lower_bound = Number(curationForm.eclean_std_values.value_range.lower_bound)
            }

            if (!isNaN(Number(curationForm.eclean_std_values.value_range.upper_bound)) && curationForm.eclean_std_values.value_range.upper_bound !== null) {
                upper_bound = Number(curationForm.eclean_std_values.value_range.upper_bound)
            }
        }

        if (curationForm.eclean_std_binary && curationForm.eclean_std_binary.binarization_threshold_unit_conversion) {
            binarization_units = curationForm.eclean_std_binary.binarization_threshold_unit_conversion.binarization_units;
            units_out = curationForm.eclean_std_values ? curationForm.eclean_std_values.units_out : curationForm.eclean_std_binary.binarization_threshold_unit_conversion.units_out;
        }

        let eclean_std_binary: any | null = curationForm.eclean_std_binary ? {} : null;

        if (curationForm.eclean_std_binary) {
            Object.keys(curationForm.eclean_std_binary).forEach(key => {
                const originalValue: any | null = curationForm.eclean_std_binary![key as keyof object];
                let value = originalValue;

                if (originalValue !== null) {
                    if (key === 'binarization_threshold_unit_conversion') {
                        value = [binarization_units, units_out]
                    } else if (key === 'binarization_threshold') {
                        value = Number(originalValue)
                    } 
                } else {
                    if (key === 'binary_value_column' || key === 'smiles_column') {
                        value = originalValue ? originalValue : undefined;
                    }
                }

                eclean_std_binary[key] = value;

            })
        }

        const form: any = {
            eclean_std_smiles: curationForm.eclean_std_smiles,
            eclean_std_values: curationForm.eclean_std_values ? {
                ...curationForm.eclean_std_values,
                value_range: [lower_bound, upper_bound],
            } : null,
            eclean_std_binary_values: curationForm.eclean_std_binary_values,
            eclean_std_regression: curationForm.eclean_std_regression ? {
                ...curationForm.eclean_std_regression,
                smiles_column: curationForm.eclean_std_regression.smiles_column ? curationForm.eclean_std_regression.smiles_column : undefined,
            } : null,
            eclean_std_binary,
        }

        Object.keys(form).forEach(key => {
            const value = form[key];
            if (value !== null) {
                stages.push({
                    stage_type: key as AcAirflowRequestsModelsDagRequestsEcleanEcleanCurationStageStageType,
                    stage_params: value,
                })
            }
        });

        return stages;
    };

    const handleGenerateDataset = async (
        e: MouseEvent<HTMLButtonElement>, 
        isCuration: boolean, 
        curationType: CurationTypes,
        datasetForm: EcleanCurationDAGRequestModel, 
        datasetCurationForm: EcleanCuration, 
        metadataForm: SpecifyColumnMetadataType,
    ) => {
        e.preventDefault();

        let stages: AcAirflowRequestsModelsDagRequestsEcleanEcleanCurationStage[] = [];
        let input_units: string | null = null;
        let output_dataset_types: EcleanCurationDAGRequestModelOutputDatasetTypesItem[] = [];

        if (isCuration) {
            stages = formatEcleanStages(datasetCurationForm);
            input_units = datasetCurationForm.input_units;
            let dsType: EcleanCurationDAGRequestModelOutputDatasetTypesItem = 'structures-only';
            if (curationType === 'single-class' || curationType === 'continuous-single') {
                dsType = 'single-class-label'
            } else if (curationType === 'continuous-continuous') {
                dsType = 'continuous-value'
            }
            output_dataset_types = [dsType];
        }   
        
        const specified_column_annotations = getColumnAnnotations(metadataForm);
                
        const form: EcleanCurationDAGRequestModel = {
            ...datasetForm,
            tag_behavior: 'define',
            stages,
            specified_column_annotations,
            output_dataset_types: output_dataset_types.length > 0 ? output_dataset_types : undefined,
            input_units: input_units !== null ? input_units : undefined,
        };

        setIsDisabled(true);
        setIsLoading(true);

        try {
            await generateDatasetMutation.mutateAsync({ data: form });
            refetchFn && refetchFn();
            setIsSuccess && setIsSuccess(true);


        } catch (err:any) {
            console.log(err);
            setPopupMessage('There was an error generating this dataset. Please try again.', false);
        }

        setIsDisabled(false);
        setIsLoading(false);
    };

    const handleCurateDataset = async (
        e: MouseEvent<HTMLButtonElement>, 
        selectedDS: Dataset | null, 
        submittedForm: EcleanCuration,
        curationType: CurationTypes,
    ) => {
        e.preventDefault();
        if (!selectedDS) return;

        const stages = formatEcleanStages(submittedForm);
        const input_units = submittedForm.input_units;
        let output_dataset_types: EcleanCurationDAGRequestModelOutputDatasetTypesItem[] = [];

        let dsType: EcleanCurationDAGRequestModelOutputDatasetTypesItem = 'structures-only';
        if (curationType === 'single-class' || curationType === 'continuous-single') {
            dsType = 'single-class-label'
        } else if (curationType === 'continuous-continuous') {
            dsType = 'continuous-value'
        }
        output_dataset_types = [dsType];

        const form: EcleanCurationDAGRequestModel = {
            dataset_hash: selectedDS.file_hash!,
            name: selectedDS.name!,
            created_by: selectedDS.created_by!,
            owner: selectedDS.owner!,
            stages,
            output_dataset_types,
            input_units: input_units !== null ? input_units : undefined,
        }

        setIsDisabled(true);
        setIsLoading(true); 

        try {
            await curationMutation.mutateAsync({ data: form });
            setIsSuccess && setIsSuccess(true);
        } catch (err:any) {
            console.log(err);
            setPopupMessage('There was an error curating this dataset. Please try again.', false);
        }

        setIsDisabled(false);
        setIsLoading(false);

    };


    const handleTrainModel = async (
        e: MouseEvent<HTMLButtonElement>, 
        trainingForm: ACTrainModelDAGRequestModel, 
        dcmConfig?: {
            isDcmMode: boolean;
            dcmMethod: string;
            dcmUnifiedSourceName: string;
        },
    ) => {
        e.preventDefault();

        const form: ACTrainModelDAGRequestModel = {
            ...trainingForm,
            tag_behavior: 'define',
            sampling_method: trainingForm.sampling_method ? trainingForm.sampling_method : undefined,
        };

        if (dcmConfig?.isDcmMode) {
            const selectedDcmMethod = dcmConfig.dcmMethod || (Array.isArray(trainingForm.models) ? (trainingForm.models[0] as string | undefined) : undefined) || 'RandomForestClassifier';
            const modelSpecifications: any = {
                mmv2_type: 'predefined',
                is_dcm: true,
                predefined_mmv2_type: selectedDcmMethod,
                dcm_unified_source_name: dcmConfig.dcmUnifiedSourceName || 'ChEMBL',
            };

            form.models = [selectedDcmMethod];
            form.megamodels_v2_train_nestedcv_task = {
                ...(form.megamodels_v2_train_nestedcv_task ?? {}),
                dag_task_input_type: 'megamodels_v2-train+nestedcv',
                dag_task_input_params: {
                    ...(form.megamodels_v2_train_nestedcv_task as any)?.dag_task_input_params,
                    model_specifications: {
                        ...((form.megamodels_v2_train_nestedcv_task as any)?.dag_task_input_params?.model_specifications ?? {}),
                        ...modelSpecifications,
                    },
                    dataset: {
                        ...((form.megamodels_v2_train_nestedcv_task as any)?.dag_task_input_params?.dataset ?? {}),
                        structure_col: trainingForm.dataset?.structure_col,
                        fingerprint_type: trainingForm.fp_type,
                    },
                },
            } as any;
        }

        setIsDisabled(true);
        setIsLoading(true);


        try {
            await trainModelMutation.mutateAsync({ data: form });
            setIsSuccess && setIsSuccess(true);

        } catch (err:any) {
            console.log(err);
            setPopupMessage('There was an error training this model. Please try again.', false);

        }

        setIsDisabled(false);
        setIsLoading(false);

    };


    const handleBatchPrediction = async (
        e: MouseEvent<HTMLButtonElement>, 
        selectedModelOptions: {
            id: string | number,
            name: string,
        }[], 
        predForm: ACPredictDAGRequestModel,
        modelTypes: string[],
) => {
        e.preventDefault();

        let model_family_id_list: any = {};

        selectedModelOptions.forEach(selectedModelOptions => {
            model_family_id_list[selectedModelOptions.id.toString()] = modelTypes
        })

        const pForm: ACPredictDAGRequestModel = {
            ...predForm,
            tag_behavior: 'define',
            model_family_id_list,
        };

        setIsDisabled(true);
        setIsLoading(true);

        try {
            await batchPredictionMutation.mutateAsync({ data: pForm });
            setIsSuccess && setIsSuccess(true);

        } catch (err:any) {
            console.log(err);
            setPopupMessage('There was an error generating this prediction. Please try again.', false);

        }

        setIsDisabled(false);
        setIsLoading(false);
    };

    const handleSinglePrediction = async (
        e: MouseEvent<HTMLButtonElement>, 
        predForm: ACSinglePredictDAGRequestModel, 
        selectedModels: { id: string | number, name: string }[],
        selectedSmiles: string,
        modelTypes: string[],
    ) => {
        e.preventDefault();

        let model_family_id_list: any = {};

        selectedModels.forEach(selectedModel => {
            model_family_id_list[selectedModel.id.toString()] = modelTypes
        })

        const pForm: ACSinglePredictDAGRequestModel = {
            ...predForm,
            tag_behavior: 'define',
            smiles: selectedSmiles,
            model_family_id_list,
            // model_family_id_list: selectedModels.map(model => Number(model.id!)),
        };

        setIsDisabled(true);
        setIsLoading(true);

        try {
            await singlePredictionMutation.mutateAsync({ data: pForm });
            setIsSuccess && setIsSuccess(true);

        } catch (err:any) {
            console.log(err);
            setPopupMessage('There was an error generating this prediction. Please try again.', false);

        }

        setIsDisabled(false);
        setIsLoading(false);
    };

    const handleReadacrossPrediction = async (
        e: MouseEvent<HTMLButtonElement>, 
        raForm: ReadAcrossDAGRequestModel, 
        raSmiles: string, 
        raNumNeighbors: number
    ) => {
        e.preventDefault();

        const form: ReadAcrossDAGRequestModel = {
            ...raForm,
            smiles: raSmiles,
            num_neighbors: raNumNeighbors,
        };

        setIsDisabled(true);
        setIsLoading(true);

        try {
            await readacrossPredictionMutation.mutateAsync({ data: form });
            setIsSuccess && setIsSuccess(true);

        } catch (err:any) {
            console.log(err);
            setPopupMessage && setPopupMessage('There was an error generating this read-across prediction. Please try again.', false);
        }

        setIsDisabled(false);
        setIsLoading(false);
    };

    const handleGenerateVisualization = async (
        e: MouseEvent<HTMLButtonElement>, 
        visForm: DatasetVisualizationDAGRequestModel,
        resultsetId: number,
    ) => {
        e.preventDefault();

        setIsDisabled(true);
        setIsLoading(true);

        const form: DatasetVisualizationDAGRequestModel = {
            ...visForm,
            data_input_specification: {
                input_type: 'resultset_list',
                input_value: [resultsetId]
            }
        }

        try {
            await generateVisualizationMutation.mutateAsync({ data: form });
            setIsSuccess && setIsSuccess(true);
        } catch (err:any) {
            console.log(err);
            setPopupMessage && setPopupMessage('There was an error generating this visualization. Please try again.', false);
        }

        setIsDisabled(false);
        setIsLoading(false);
        
    };

    return {
        handleUploadFile,
        handleGenerateDataset,
        handleCurateDataset,
        handleTrainModel,
        handleBatchPrediction,
        handleSinglePrediction,
        handleReadacrossPrediction,
        handleGenerateVisualization,
    }
};

export default usePost;